import {
  collection, doc, addDoc, updateDoc, deleteDoc, getDocs, query, where, orderBy, onSnapshot, serverTimestamp, limit
} from 'firebase/firestore';
import { db } from '../firebase';

const norm = (v) => (v || '').toString().trim();
const normKey = (v) => norm(v).toUpperCase().replace(/\s+/g, '');

// Annuaire des clients du cabinet (personnes physiques : CIN, sociétés : ICE)
export class ClientService {
  static subscribe(cabinetId, callback) {
    try {
      const q = query(collection(db, 'clients'), where('cabinetId', '==', cabinetId), orderBy('nom'), limit(1000));
      return onSnapshot(q, (snap) => {
        const items = [];
        snap.forEach((d) => items.push({ id: d.id, ...d.data() }));
        callback(items);
      }, (error) => {
        console.error('Erreur clients:', error);
        callback([]);
      });
    } catch (error) {
      console.error('Erreur config clients:', error);
      return () => {};
    }
  }

  static async getAll(cabinetId) {
    const snap = await getDocs(query(collection(db, 'clients'), where('cabinetId', '==', cabinetId), orderBy('nom')));
    const items = [];
    snap.forEach((d) => items.push({ id: d.id, ...d.data() }));
    return items;
  }

  // Doublon CIN / ICE dans le cabinet (comparaison insensible à la casse/espaces)
  static findDuplicate(clients, data, excludeId = null) {
    const key = data.type === 'societe' ? normKey(data.ice) : normKey(data.cin);
    if (!key) return null;
    return clients.find((c) => c.id !== excludeId && (
      (data.type === 'societe' && normKey(c.ice) === key) || (data.type !== 'societe' && normKey(c.cin) === key)
    )) || null;
  }

  static async add(cabinetId, data) {
    try {
      const ref = await addDoc(collection(db, 'clients'), {
        cabinetId,
        type: data.type === 'societe' ? 'societe' : 'personne',
        nom: norm(data.nom),
        cin: data.type === 'societe' ? '' : norm(data.cin),
        ice: data.type === 'societe' ? norm(data.ice) : '',
        telephone: norm(data.telephone),
        email: norm(data.email),
        adresse: norm(data.adresse),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { success: true, id: ref.id };
    } catch (error) {
      console.error('Erreur ajout client:', error);
      return { success: false, error: error.message };
    }
  }

  static async update(id, data) {
    try {
      const payload = { ...data, updatedAt: serverTimestamp() };
      delete payload.id;
      delete payload.cabinetId;
      await updateDoc(doc(db, 'clients', id), payload);
      return { success: true };
    } catch (error) {
      console.error('Erreur MAJ client:', error);
      return { success: false, error: error.message };
    }
  }

  static async remove(id) {
    try {
      await deleteDoc(doc(db, 'clients', id));
      return { success: true };
    } catch (error) {
      console.error('Erreur suppr client:', error);
      return { success: false, error: error.message };
    }
  }

  // Crée dans l'annuaire les clients d'un dossier qui n'y sont pas encore (par CIN/ICE, sinon par nom)
  static async ensureClients(cabinetId, existing, projectClients = []) {
    const created = [];
    for (const c of projectClients) {
      if (!norm(c.nom)) continue;
      const dup = ClientService.findDuplicate(existing, c)
        || existing.find((e) => normKey(e.nom) === normKey(c.nom) && e.type === (c.type === 'societe' ? 'societe' : 'personne'));
      if (dup) continue;
      const res = await ClientService.add(cabinetId, c);
      if (res.success) {
        const rec = { id: res.id, cabinetId, ...c };
        existing.push(rec);
        created.push(rec);
      }
    }
    return created;
  }
}
